import { z } from "zod";
import { accessFields, actorSchema, idSchema, instantSchema, sourceDateSchema } from "@amber/contracts";

export const languageCodeSchema = z.string().regex(/^[a-z]{2,3}(-[A-Z]{2})?$/, "expected a language code");
const platformKeySchema = z.string().regex(/^[a-z][a-z0-9_]*$/, "expected a platform key");
const revisionSchema = z.number().int().positive();
const textSchema = z.string().min(1);

export const unknownValueSchema = z.strictObject({ state: z.literal("unknown"), reason: textSchema });

export const PROFILE_LIFECYCLE_STATES = ["draft", "active", "paused", "archived", "incidental"] as const;
export const profileLifecycleStateSchema = z.enum(PROFILE_LIFECYCLE_STATES);

export const profileRecordSchema = z.strictObject({
  id: idSchema("prf"),
  display_name: textSchema,
  lifecycle_state: profileLifecycleStateSchema,
  external_ids: z.strictObject({
    wikidata: z
      .string()
      .regex(/^Q\d+$/)
      .nullable(),
    wikipedia: z.record(languageCodeSchema, textSchema),
  }),
  birth_date: z.union([sourceDateSchema, unknownValueSchema]),
  aliases: z.array(
    z.strictObject({
      value: textSchema,
      script: z.string().regex(/^[A-Z][a-z]{3}$/, "expected an ISO 15924 script code"),
      language: languageCodeSchema,
    }),
  ),
  picture: z
    .strictObject({
      url: z.url(),
      origin_url: z.url(),
      rights: textSchema,
    })
    .nullable(),
  languages: z.array(languageCodeSchema),
  description: textSchema,
  merged_into: z
    .strictObject({
      profile_id: idSchema("prf"),
      merge_id: idSchema("mrg"),
    })
    .nullable(),
  ...accessFields,
  created_by: actorSchema,
  created_at: instantSchema,
  revision: revisionSchema,
});
export type ProfileRecord = z.infer<typeof profileRecordSchema>;

export const organisationRecordSchema = z.strictObject({
  id: idSchema("org"),
  name: textSchema,
  kind: z.string().regex(/^[a-z][a-z_]*$/),
  external_ids: z.strictObject({
    wikidata: z
      .string()
      .regex(/^Q\d+$/)
      .nullable(),
  }),
  ...accessFields,
  created_by: actorSchema,
  created_at: instantSchema,
  revision: revisionSchema,
});
export type OrganisationRecord = z.infer<typeof organisationRecordSchema>;

export const AFFILIATION_KINDS = ["membership", "leadership", "office", "representation", "public_role"] as const;
export const DERIVATIONS = ["sourced", "ai_inferred", "editor"] as const;
export const VERIFICATION_STATUSES = ["unverified", "verified"] as const;

export const affiliationRecordSchema = z.strictObject({
  id: idSchema("aff"),
  profile_id: idSchema("prf"),
  organisation_id: idSchema("org"),
  kind: z.enum(AFFILIATION_KINDS),
  title: textSchema,
  start: z.union([sourceDateSchema, unknownValueSchema]),
  end: z.union([sourceDateSchema, z.strictObject({ state: z.literal("ongoing") }), unknownValueSchema]),
  provenance: z.strictObject({
    derivation: z.enum(DERIVATIONS),
    evidence_url: z.url().nullable(),
    observed_at: instantSchema,
  }),
  verification_status: z.enum(VERIFICATION_STATUSES),
  protected: z.boolean(),
  revision: revisionSchema,
});
export type AffiliationRecord = z.infer<typeof affiliationRecordSchema>;

export const PRESENCE_RELATIONSHIPS = [
  "personal",
  "office",
  "organisation",
  "programme_host",
  "guest_appearance",
  "publisher",
  "unverified_lead",
] as const;
export const presenceRelationshipSchema = z.enum(PRESENCE_RELATIONSHIPS);

export const presenceRecordSchema = z.strictObject({
  id: idSchema("prs"),
  profile_id: idSchema("prf"),
  platform: platformKeySchema,
  stable_platform_id: textSchema,
  handles: z.array(
    z.strictObject({
      handle: textSchema,
      observed_from: instantSchema,
      observed_to: instantSchema.nullable(),
    }),
  ),
  relationship: presenceRelationshipSchema,
  source_id: idSchema("src"),
  verified_at: instantSchema.nullable(),
  valid_from: sourceDateSchema.nullable(),
  valid_to: sourceDateSchema.nullable(),
  evidence: z.array(z.strictObject({ kind: z.literal("url"), url: z.url() })),
  revision: revisionSchema,
});
export type PresenceRecord = z.infer<typeof presenceRecordSchema>;

export const PROFILE_MERGE_STATES = ["started", "interrupted", "completed", "reversed"] as const;

export const profileMergeRecordSchema = z
  .strictObject({
    id: idSchema("mrg"),
    survivor_profile_id: idSchema("prf"),
    merged_profile_id: idSchema("prf"),
    state: z.enum(PROFILE_MERGE_STATES),
    steps: z.array(
      z.strictObject({
        step: z.string().regex(/^[a-z][a-z_]*$/),
        state: z.enum(["pending", "done"]),
        at: instantSchema.nullable(),
      }),
    ),
    interrupted_at: instantSchema.nullable(),
    recovered_at: instantSchema.nullable(),
    reason: textSchema,
    requested_by: actorSchema,
    started_at: instantSchema,
    completed_at: instantSchema.nullable(),
    reversed: z
      .strictObject({
        at: instantSchema,
        by: actorSchema,
        reason: textSchema,
      })
      .nullable(),
  })
  .refine((m) => m.survivor_profile_id !== m.merged_profile_id, "a profile cannot be merged into itself")
  .refine(
    (m) => (m.state === "completed" || m.state === "reversed") === (m.completed_at !== null),
    "completed_at is set exactly when the merge completed",
  )
  .refine(
    (m) => (m.state === "reversed") === (m.reversed !== null),
    "reversed is set exactly when the merge is reversed",
  );
export type ProfileMergeRecord = z.infer<typeof profileMergeRecordSchema>;

export const coveragePlanRecordSchema = z.discriminatedUnion("owner_kind", [
  z.strictObject({
    id: idSchema("pln"),
    owner_kind: z.literal("person"),
    profile_id: idSchema("prf"),
    source_id: z.null(),
    latest_revision_id: idSchema("plr"),
    applied_revision_id: idSchema("plr").nullable(),
    revision: revisionSchema,
  }),
  z.strictObject({
    id: idSchema("pln"),
    owner_kind: z.literal("shared_source"),
    profile_id: z.null(),
    source_id: idSchema("src"),
    latest_revision_id: idSchema("plr"),
    applied_revision_id: idSchema("plr").nullable(),
    revision: revisionSchema,
  }),
]);
export type CoveragePlanRecord = z.infer<typeof coveragePlanRecordSchema>;

export const CADENCE_TIERS = ["priority", "standard", "low"] as const;
export const cadenceTierSchema = z.enum(CADENCE_TIERS);

export const PLAN_REVISION_STATES = ["recorded", "blocked", "applied", "superseded"] as const;
export const planRevisionStateSchema = z.enum(PLAN_REVISION_STATES);

export const planRevisionRecordSchema = z
  .strictObject({
    id: idSchema("plr"),
    plan_id: idSchema("pln"),
    revision: revisionSchema,
    base_revision: revisionSchema.nullable(),
    state: planRevisionStateSchema,
    rationale: textSchema,
    responsible_editor: idSchema("usr"),
    languages: z.array(languageCodeSchema),
    sources: z.array(
      z.strictObject({
        source_id: idSchema("src"),
        cadence_tier: cadenceTierSchema,
        required: z.boolean(),
      }),
    ),
    monitoring_start: instantSchema,
    historical_window: z.strictObject({
      back_to: z.iso.date(),
      precision: z.enum(["year", "month", "day"]),
    }),
    exclusions: z.array(z.strictObject({ description: textSchema, reason: textSchema })),
    review_date: z.iso.date(),
    reason: textSchema,
    recorded_by: actorSchema,
    recorded_at: instantSchema,
    applied_at: instantSchema.nullable(),
    superseded_at: instantSchema.nullable(),
    blocked_by: z.array(
      z.strictObject({
        source_id: idSchema("src"),
        suggested_source_id: idSchema("sug").nullable(),
        reason: textSchema,
      }),
    ),
  })
  .refine(
    (r) => (r.base_revision === null ? r.revision === 1 : r.base_revision < r.revision),
    "base_revision must precede revision",
  )
  .refine(
    (r) => (r.state === "blocked") === r.blocked_by.length > 0,
    "blocked_by is non-empty exactly when the revision is blocked",
  )
  .refine(
    (r) => (r.state === "applied" ? r.applied_at !== null : r.state === "superseded" || r.applied_at === null),
    "applied_at is set exactly when the revision applied",
  )
  .refine(
    (r) => (r.state === "superseded") === (r.superseded_at !== null),
    "superseded_at is set exactly when superseded",
  );
export type PlanRevisionRecord = z.infer<typeof planRevisionRecordSchema>;

export const SUGGESTED_SOURCE_STATES = ["open", "approved", "rejected"] as const;

export const suggestedSourceRecordSchema = z
  .strictObject({
    id: idSchema("sug"),
    profile_id: idSchema("prf"),
    source_id: idSchema("src").nullable(),
    presence_id: idSchema("prs").nullable(),
    candidate: z.strictObject({ platform: platformKeySchema, url: z.url() }).nullable(),
    why_suggested: textSchema,
    relationship: presenceRelationshipSchema,
    state: z.enum(SUGGESTED_SOURCE_STATES),
    decided_by: actorSchema.nullable(),
    decided_at: instantSchema.nullable(),
    reason: textSchema.nullable(),
    created_at: instantSchema,
  })
  .refine(
    (s) => s.source_id !== null || s.presence_id !== null || s.candidate !== null,
    "a suggestion names a source, presence or candidate",
  )
  .refine(
    (s) => (s.state === "open") === (s.decided_by === null && s.decided_at === null),
    "decided_by and decided_at are set exactly when decided",
  )
  .refine((s) => s.state !== "rejected" || s.reason !== null, "a rejection requires a reason");
export type SuggestedSourceRecord = z.infer<typeof suggestedSourceRecordSchema>;

export const RECHECK_STATES = ["queued", "running", "finished"] as const;
const countSchema = z.number().int().nonnegative();

export const recheckRecordSchema = z
  .strictObject({
    id: idSchema("rck"),
    profile_id: idSchema("prf"),
    trigger: z.enum(["first_plan_applied", "reactivated"]),
    plan_revision_id: idSchema("plr"),
    state: z.enum(RECHECK_STATES),
    progress: z.strictObject({
      items_checked: countSchema,
      items_total: countSchema.nullable(),
      found: countSchema,
      found_excluded_not_relevant: countSchema,
    }),
    started_at: instantSchema.nullable(),
    finished_at: instantSchema.nullable(),
  })
  .refine((r) => (r.state === "queued") === (r.started_at === null), "started_at is set once the recheck runs")
  .refine((r) => (r.state === "finished") === (r.finished_at !== null), "finished_at is set exactly when finished");
export type RecheckRecord = z.infer<typeof recheckRecordSchema>;

export const exampleRecordSchemas: Record<string, z.ZodType> = {
  "profiles.json": profileRecordSchema,
  "organisations.json": organisationRecordSchema,
  "affiliations.json": affiliationRecordSchema,
  "presences.json": presenceRecordSchema,
  "profile-merges.json": profileMergeRecordSchema,
  "coverage-plans.json": coveragePlanRecordSchema,
  "plan-revisions.json": planRevisionRecordSchema,
  "suggested-sources.json": suggestedSourceRecordSchema,
  "rechecks.json": recheckRecordSchema,
};
